import { Gamepad2, Cloud, Zap, Users, ShieldCheck, Smartphone } from "lucide-react";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async"; 
import { FaRocket } from "react-icons/fa";

const WhyChooseUs = () => {

useEffect(() => {
  const loadAOS = async () => {
    const AOS = await import('aos');
    AOS.init({
      duration:500,
      easing: "ease-out-cubic",
      once: true,
      offset: 100
    });
  };
  loadAOS();
}, []);

// 🚀 EXPANDED CONTENT FOR HIGHER VALUE & SEO
const features = [
  {
    title: "Instant Play - No Downloads",
    icon: <Zap className="w-10 h-10 text-yellow-400" aria-hidden="true" />,
    desc: "Save your device's valuable storage space! By utilizing advanced HTML5 canvas and WebGL rendering technology, our platform allows you to play free online games instantly. Skip the annoying app store downloads, avoid lengthy installations, and never worry about background updates. Just click on your favorite title and jump straight into the action.",
  },
  {
    title: "100% Safe & Secure Gaming",
    icon: <ShieldCheck className="w-10 h-10 text-green-400" aria-hidden="true" />,
    desc: "Your digital safety is our absolute priority. Game Zone Hub provides a heavily moderated, secure environment for free browser games. Because our games run in a sandboxed web environment, we guarantee a completely malware-free experience. Enjoy peace of mind with zero hidden fees, no shady background scripts, and family-friendly content suitable for players of all ages.",
  },
  {
    title: "Mobile Optimized Experience",
    icon: <Smartphone className="w-10 h-10 text-blue-400" aria-hidden="true" />,
    desc: "Experience true, seamless cross-platform gaming. Whether you are using an Apple iPhone, an Android smartphone, an iPad tablet, or a high-end desktop PC, our responsive web design ensures flawless, full-screen gameplay. The games automatically adapt to your screen resolution, offering intuitive touch-swipe controls for mobile and precise keyboard/mouse mapping for desktop users.",
  },
  {
    title: "Massive & Diverse Game Library",
    icon: <Gamepad2 className="w-10 h-10 text-pink-400" aria-hidden="true" />,
    desc: "Never run out of entertainment with our endlessly growing catalog. We update our massive library daily with the highest-rated titles across every genre imaginable. From intense multiplayer action and high-speed 3D racing to brain-teasing logic puzzles and relaxing casual simulators, we ensure you always have fresh, high-quality free online games to discover.",
  },
];

 const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": features.map((feature, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": feature.title,
      "description": feature.desc
    }))
};

  return (
    <section className="bg-gradient-to-b lg:px-8 from-[#0f172a] to-[#1e293b] sm:py-20 py-12">
       <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4 ">
        
        {/* 🚀 EXPANDED HEADER TEXT */}
        <div className="text-center mb-16">
            <h2
            className="text-center text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-extrabold text-white mb-6 flex flex-wrap justify-center items-center gap-4"
            data-aos="fade-up"
            data-aos-delay="100"
            >
            <FaRocket className="text-cyan-400 animate-bounce sm:animate-none sm:hover:-translate-y-1 transition-transform duration-300" />
            <div>
                <span className="whitespace-nowrap">Why Choose{" "}</span>
                <span className="bg-clip-text whitespace-nowrap text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">
                Game Zone Hub?
                </span>
            </div>
            </h2>
            <div data-aos="fade-up" data-aos-delay="200" className="space-y-4 max-w-4xl mx-auto text-gray-400 sm:text-lg text-[16px] leading-relaxed">
                <p>
                    In an era filled with expensive gaming consoles, massive file downloads, and aggressive microtransactions, <strong>Game Zone Hub</strong> stands out as the ultimate, trusted destination to <strong>play free online games</strong>. Our mission is simple: to provide frictionless, instant entertainment for everyone, directly through the web browser.
                </p>
                <p>
                    Unlike heavily monetized sites, we focus entirely on providing a safe, lightning-fast, and highly optimized user experience. Our premium HTML5 architecture ensures lag-free performance, allowing both casual players and hardcore gamers to enjoy top-tier <strong>free browser games</strong> without hardware restrictions.
                </p>
            </div>
        </div>

        {/* FEATURES GRID */}
        <ul
          className="grid lg:grid-cols-4 md:grid-cols-2 gap-8"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          {features.map((feature, index) => (
            <li
              key={index}
              data-aos="zoom-in"
              data-aos-delay={`${300 + index * 100}`}
              className="relative bg-[#1e293b] rounded-3xl cursor-pointer p-8 text-center
                         border border-white/5 hover:border-cyan-400/50
                         transition-all duration-500 group
                         shadow-[0_10px_30px_rgba(0,0,0,0.5)] 
                         hover:shadow-[0_0_40px_rgba(0,200,255,0.3)]
                         hover:scale-105 hover:-translate-y-2 overflow-hidden flex flex-col h-full"
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                <div className="absolute top-0 left-0 w-40 h-40 bg-cyan-400/10 blur-3xl rounded-full"></div>
                <div className="absolute bottom-0 right-0 w-40 h-40 bg-purple-500/10 blur-3xl rounded-full"></div>
              </div>

              <div
                className="flex justify-center mb-6 relative z-10 group-hover:scale-110 transition-transform duration-300 bg-black/20 w-20 h-20 mx-auto rounded-2xl items-center border border-white/5 shadow-inner"
                data-aos="flip-up"
                data-aos-delay={`${350 + index * 100}`}
              >
                {feature.icon}
              </div>

              <h3
                className="sm:text-xl text-lg font-bold text-white mb-4 relative z-10 group-hover:text-cyan-300 transition-colors duration-300"
                data-aos="fade-up"
                data-aos-delay={`${400 + index * 100}`}
              >
                {feature.title}
              </h3>

              <p
                className="text-gray-400 text-sm relative z-10 group-hover:text-gray-300 transition-colors duration-300 leading-relaxed flex-grow"
                data-aos="fade-up"
                data-aos-delay={`${450 + index * 100}`}
              >
                {feature.desc}
              </p>

              <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
            </li>
          ))}
        </ul>

      </div>
    </section>
  );
};

export default WhyChooseUs;